/**
 * Start: the brief field IS the home screen.
 *
 * Composition only - `BriefScreen` and the setup blocks own their own data
 * and still own their own data. What changes is the order of the argument:
 * the thing you came to do is first, and the setup material sits underneath,
 * hiding itself once it has nothing left to say.
 *
 * The greeting was the whole of the old Overview ("Everything you need, in one
 * place") and gave the first screen no job at all. It survives as one quiet
 * line above the compose field rather than as a hero.
 *
 * @module web/screens/app/StartSection
 */
import { useState } from "react";
import { BriefScreen } from "./BriefScreen.tsx";
import { useBrand } from "../../state/BrandContext.tsx";
import { Checklist } from "./overview/Checklist.tsx";
import { Integrations } from "./overview/Integrations.tsx";
import { ActionItems } from "./work/ActionItems.tsx";

export function StartSection({ name, submissionId = null }: {
  name: string | null;
  /** `#start/<submissionId>`: show that submission's price block instead of an empty field. */
  submissionId?: string | null;
}) {
  // "Welcome back" to someone who has just finished the intro slides is a small
  // lie at the exact moment the product should feel like it knows them. A brand
  // with a brief behind it is back; a brand without one has just arrived.
  const { brand } = useBrand();
  const [contentFocus, setContentFocus] = useState(0);
  const [contentRevision, setContentRevision] = useState(0);
  const returning = !!brand?.checklist?.grandfathered;
  return (
    <>
      <p className="start-greeting fade-in">
        {returning ? "Welcome back" : "Welcome"}<em className="t-emph">,</em> {name ?? "there"}.
      </p>

      <BriefScreen submissionId={submissionId} onAddContent={() => setContentFocus(n => n + 1)} contentRevision={contentRevision} />

      <ActionItems compact />

      {brand && !brand.contactName && <div className="card start-profile">
        <p className="t-body-sm">Finish your profile so your team knows who is briefing.</p>
        <a className="btn btn-ghost" href="#settings" style={{ marginTop: 12 }}>Add your name</a>
      </div>}

      {(brand?.role ?? "owner") === "owner" && <div className="start-setup">
        <Checklist focusContent={contentFocus} onContentAttached={() => setContentRevision(n => n + 1)} />
        <Integrations />
      </div>}
    </>
  );
}
